import { Button, ButtonWithLoader, Col, Row, Wizard } from '@valid-eval/shared-react-components';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ContentPreview } from 'components/ContentsPreview';
import RichText from 'components/Form/RichText';
import { minLength } from 'components/FormFields/validations';
import { useEditEventContents, useSaveContents } from 'data/features/contentsHooks';
import { useDebouncedCallback } from 'utils/debounce-promise';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';
import { judgeConfirmationURL } from 'utils/urls';

import { EventStepComponentProps } from '.';
import { DetailsList } from './components/DetailsList';
import EditEventStepForm from './components/EditEventStepForm';

type JudgesStepForm = {
  contents_judge_signup_instructions?: string;
  contents_judge_signup_confirmation_notice?: string;
};

function JudgesStepComponent({ event, label, subLabel, nextStep }: EventStepComponentProps) {
  const { t } = useTranslation();
  const { contents } = useEditEventContents(event.id);
  const { saveContents, saving } = useSaveContents();
  const showErrorIfRejected = useShowErrorIfRejected();

  const methods = useForm<JudgesStepForm>({
    mode: 'onBlur',
    values: {
      contents_judge_signup_instructions: contents.contents_judge_signup_instructions ?? '',
      contents_judge_signup_confirmation_notice:
        contents.contents_judge_signup_confirmation_notice ?? '',
    },
  });

  const handleNextStep = async () => {
    await methods.handleSubmit(save)();
    nextStep();
  };

  const debouncedSave = useDebouncedCallback(
    async (data: JudgesStepForm) => {
      const errorKeys = Object.keys(methods.formState.errors);
      for (const errorKey of errorKeys) {
        data[errorKey as keyof JudgesStepForm] = undefined;
      }
      save(data);
    },
    [methods.formState.errors],
    500,
  );

  // Submit form when values change
  useEffect(() => {
    const subscription = methods.watch((_, { type }) => {
      if (type !== 'change') return;
      methods.trigger();
      debouncedSave(methods.getValues());
    });
    return subscription.unsubscribe;
  }, [methods.watch]);

  const save = async (data: JudgesStepForm) => {
    showErrorIfRejected(await saveContents(event.id, data));
  };

  const { contents_judge_signup_instructions, contents_judge_signup_confirmation_notice } =
    methods.getValues();

  return (
    <EditEventStepForm methods={methods} onSubmit={save} label={label} subLabel={subLabel}>
      <Row className="mt-3">
        <Col md={12}>
          <RichText
            required
            name="contents_judge_signup_instructions"
            label={t('event.form.contents_judge_signup_instructions')}
            rules={{ validate: { minLength: minLength(20) } }}
          />
          <ContentPreview content={contents_judge_signup_instructions ?? ''} />
        </Col>
        <Col md={12} className="mt-3">
          <a
            className={'form-control text-blue01 fw-bold'}
            href={judgeConfirmationURL(event.id)}
            target="_blank"
            rel="noreferrer"
          >
            <span className="text-blue01 me-2">{t('help.judge_signup')}</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-blue02" />
          </a>
        </Col>
        <Col>
          <Wizard.NavDivider />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <RichText
            required
            name="contents_judge_signup_confirmation_notice"
            label={t('event.form.contents_judge_signup_confirmation_notice')}
            rules={{ validate: { minLength: minLength(20) } }}
          />
          <ContentPreview content={contents_judge_signup_confirmation_notice ?? ''} />
        </Col>
        <Col md={12} className="mt-3">
          <a
            className={'form-control text-blue01 fw-bold'}
            href={judgeConfirmationURL(event.id)}
            target="_blank"
            rel="noreferrer"
          >
            <span className="text-blue01 me-2">{t('help.judge_confirmation')}</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-blue02" />
          </a>
        </Col>
        <Col>
          <Wizard.NavDivider />
        </Col>
      </Row>
      <DetailsList details={event.judge_details} title={t('event.form.judge_details_title')} />
      <Wizard.Footer>
        <div className="d-flex justify-content-end align-items-center">
          <Button variant="secondary" className="me-3" type="submit" disabled={saving}>
            {t('event.form.save_progress')}
          </Button>
          <ButtonWithLoader variant="primary" loading={saving} onClick={handleNextStep}>
            {t('event.form.next')}
          </ButtonWithLoader>
        </div>
      </Wizard.Footer>
    </EditEventStepForm>
  );
}

export default JudgesStepComponent;
